import type { ExcelDownloadClient } from "app/components/React/ExcelButton/ExcelClientDownload";
import type { CompanyAnalysisTechnologyItem } from "app/pages/sales-intelligence/modules/technology/types/company-analysis-technology-filter";
import type { GetRequestOptions, IFetchService } from "app/services/fetchService";

import type { CompanyBaseInfo, CompanyCard } from "../../types/company";
import type { CompanyAnalysisCompetititors } from "../../types/competitors";
import type { DigitalAssetsRecordsResponseDto, DigitalAssetType } from "../../types/digital-assets";
import type { GeoBreakdown } from "../../types/geo-breakdown";
import type { BaseSettingsDto } from "../../types/page-base-settings";
import type { SignalEventsDataDto, SignalEventsRequestParams } from "../../types/signals";

const BASE_OVERVIEW_API_URL = "/sales-api/account-overview";
const BASE_COMPANY_ANALYSIS_API_URL = "/sales-api/company-analysis";

export default function createCompanyAnalysisApiService(
    fetchService: IFetchService,
    excelDownloadClient: ExcelDownloadClient,
) {
    return {
        fetchBaseSettings(domain: string, options?: GetRequestOptions): Promise<BaseSettingsDto> {
            return fetchService.get(`${BASE_OVERVIEW_API_URL}/settings/${domain}`, undefined, options);
        },
        fetchCompanyBaseInfo(domain: string, options?: GetRequestOptions): Promise<CompanyBaseInfo> {
            return fetchService.get(`${BASE_COMPANY_ANALYSIS_API_URL}/base-info`, { domain }, options);
        },
        fetchCompanyDomainInfo(domain: string, options?: GetRequestOptions): Promise<CompanyBaseInfo> {
            return fetchService.get(`${BASE_COMPANY_ANALYSIS_API_URL}/domain-info`, { domain }, options);
        },
        fetchCompanyCard(domain: string, options?: GetRequestOptions): Promise<CompanyCard> {
            return fetchService.get(`${BASE_COMPANY_ANALYSIS_API_URL}/id-card`, { domain }, options);
        },
        fetchSignalEvents(
            params: SignalEventsRequestParams,
            options?: GetRequestOptions,
        ): Promise<SignalEventsDataDto> {
            return fetchService.get(
                `${BASE_COMPANY_ANALYSIS_API_URL}/signals`,
                {
                    ...params,
                    take: params.take || 500,
                },
                options,
            );
        },
        reportECommerceOverview(domain: string): Promise<void> {
            return fetchService.post(`${BASE_OVERVIEW_API_URL}/widget/ecommerce-info/report?domain=${domain}`);
        },
        fetchDigitalAssets<T extends DigitalAssetType>(
            domain: string,
            assetType: T,
            options?: GetRequestOptions,
        ): Promise<DigitalAssetsRecordsResponseDto<T>> {
            return fetchService.get(
                `${BASE_COMPANY_ANALYSIS_API_URL}/digital-assets/${assetType}`,
                { domain },
                options,
            );
        },
        fetchGeoBreakdown(domain: string, options?: GetRequestOptions): Promise<GeoBreakdown[]> {
            return fetchService.get(`${BASE_COMPANY_ANALYSIS_API_URL}/geography`, { domain }, options);
        },
        fetchCompanyTechnologies(
            domain: string,
            options?: GetRequestOptions,
        ): Promise<CompanyAnalysisTechnologyItem[]> {
            return fetchService.get(`${BASE_COMPANY_ANALYSIS_API_URL}/technologies`, { domain }, options);
        },
        fetchCompanyCompetitors(
            { domain, limit, includeSignalsCount }: { limit?: number; includeSignalsCount?: boolean; domain: string },
            options?: GetRequestOptions,
        ): Promise<CompanyAnalysisCompetititors> {
            return fetchService.get(
                `${BASE_COMPANY_ANALYSIS_API_URL}/competitors`,
                { domain, limit, includeSignalsCount },
                options,
            );
        },
        downloadCompetitorsExcel({ domain, competitors }: { domain: string; competitors: string[] }) {
            return excelDownloadClient(`${BASE_COMPANY_ANALYSIS_API_URL}/competitors/excel?domain=${domain}`, {
                domains: competitors,
            });
        },
        downloadTechnologiesExcel(domain: string) {
            return `${BASE_COMPANY_ANALYSIS_API_URL}/technologies/excel?domain=${domain}`;
        },
    };
}
